import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../../common/LanguageSelector';
import CompanySelector from '../../common/CompanySelector';
import DynamicLogo from '../../common/DynamicLogo';
import LoginService from '../../../services/LoginService';
import ProfileService from '../../../services/ProfileService';
import { convertToImageUrl } from '../../../utils/imageUrlHelper';
import AlertService from '../../../services/AlertService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import MenuService from '../../../services/MenuService';
import { useTabs } from '../../../contexts/TabContext';

const normalizeRoutePath = (pathname) => {
  if (!pathname || pathname === '/') return '/';
  let normalized = String(pathname).trim();
  if (normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }
  if (normalized.endsWith('/add') || normalized.endsWith('/edit')) {
    normalized = normalized.substring(0, normalized.lastIndexOf('/'));
  }
  if (normalized.endsWith('-add') || normalized.endsWith('-edit')) {
    normalized = normalized.replace(/-(add|edit)$/i, '');
  }
  return normalized || '/';
};

const routeMatches = (expectedRoute, currentRoute) => {
  if (!expectedRoute || !currentRoute) return false;
  const left = normalizeRoutePath(expectedRoute);
  const right = normalizeRoutePath(currentRoute);
  return (
    right === left
    || right.startsWith(`${left}/`)
    || right.startsWith(`${left}-`)
  );
};

const resolveAlertsReadPermissionFromMenu = (menus) => {
  const targetRoute = '/alerts';
  for (const menu of Array.isArray(menus) ? menus : []) {
    if (routeMatches(menu.route, targetRoute)) {
      return Boolean(menu.canRead);
    }
    const submenus = Array.isArray(menu.submenus) ? menu.submenus : [];
    for (const submenu of submenus) {
      if (routeMatches(submenu.route, targetRoute)) {
        return Boolean(submenu.canRead);
      }
    }
  }
  return false;
};

const Header = ({ data }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openTab } = useTabs();
  const [username, setDisplayName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [hasProfileImageError, setHasProfileImageError] = useState(false);

  // Estado para controlar la visibilidad del sidebar
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Estado para controlar el dropdown del usuario
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const [unreadAlertsCount, setUnreadAlertsCount] = useState(0);
  const [canReadAlerts, setCanReadAlerts] = useState(false);
  const canReadAlertsRef = useRef(false);
  const refreshTimerRef = useRef(null);
  const refreshInFlightRef = useRef(false);
  const nextDelayMsRef = useRef(3 * 60 * 1000);

  useEffect(() => {
    canReadAlertsRef.current = canReadAlerts;
  }, [canReadAlerts]);

  const refreshAlertsPermission = useCallback(async () => {
    try {
      const menus = await MenuService.getCurrentUserMenu();
      const allowed = resolveAlertsReadPermissionFromMenu(menus);
      setCanReadAlerts(allowed);
      if (!allowed) {
        setUnreadAlertsCount(0);
      }
    } catch (_) {
      setCanReadAlerts(false);
      setUnreadAlertsCount(0);
    }
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    try {
      if (!canReadAlertsRef.current) {
        setUnreadAlertsCount(0);
        return;
      }
      if (refreshInFlightRef.current) return;
      refreshInFlightRef.current = true;
      const count = await AlertService.getUnreadCount();
      const safe = Number.isFinite(Number(count)) ? Number(count) : 0;
      setUnreadAlertsCount(safe);
      nextDelayMsRef.current = 3 * 60 * 1000; // estado sano: refresh ligero cada 3 min
    } catch (_) {
      // Silencioso
      // no machacar a 0 si falla: mantenemos el último valor
      nextDelayMsRef.current = Math.min(nextDelayMsRef.current * 2, 10 * 60 * 1000); // backoff hasta 10m
    } finally {
      refreshInFlightRef.current = false;
    }
  }, []);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback((delayMs) => {
    clearRefreshTimer();
    const ms = Math.max(0, Number(delayMs) || 0);
    refreshTimerRef.current = setTimeout(() => {
      if (document.visibilityState !== 'visible') {
        // si está en background, reprogramar sin hacer request
        scheduleRefresh(nextDelayMsRef.current);
        return;
      }
      refreshUnreadCount().finally(() => scheduleRefresh(nextDelayMsRef.current));
    }, ms);
  }, [clearRefreshTimer, refreshUnreadCount]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    document.documentElement.classList.toggle('sidebar-left-opened');
  };

  useEffect(() => {
    const storedUsername = localStorage.getItem('displayName');
    if (storedUsername) {
      setDisplayName(storedUsername);
    }

    // Obtener el rol del usuario desde userData
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        if (userData.roles && userData.roles.description) {
          setUserRole(userData.roles.description); 
        }
      } catch (error) {
        console.error('Error parsing userData:', error);
      }
    }

    const loadProfileHeaderData = async () => {
      try {
        const profile = await ProfileService.getCurrentProfile();
        if (profile?.fullName) {
          setDisplayName(profile.fullName);
          localStorage.setItem('displayName', profile.fullName);
        }
        const normalizedProfileImage = convertToImageUrl(profile?.profileImage, '');
        if (normalizedProfileImage) {
          setProfileImage(normalizedProfileImage);
          setHasProfileImageError(false);
        } else {
          setProfileImage('');
          setHasProfileImageError(false);
        }
      } catch (_) {
        // Silent: fallback a localStorage
      }
    };

    loadProfileHeaderData();

    refreshAlertsPermission();
    // Primer refresh y luego refresh ligero con backoff (sin polling agresivo)
    refreshUnreadCount().finally(() => scheduleRefresh(nextDelayMsRef.current));

    const handleAlertsUpdated = () => {
      // Reaccionar a cambios reales (crear/abrir alertas)
      refreshUnreadCount();
      // Evitar carrera con commit/replicación: reintento corto, pero sin bucle
      setTimeout(() => refreshUnreadCount(), 700);
    };
    const handleCompanyChanged = () => {
      refreshAlertsPermission();
      refreshUnreadCount();
    };
    const handleWindowFocus = () => refreshUnreadCount();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUnreadCount();
        scheduleRefresh(nextDelayMsRef.current);
      }
    };

    const handleProfileUpdated = (event) => {
      const updatedDisplayName = event?.detail?.displayName;
      const updatedProfileImage = event?.detail?.profileImage;
      if (updatedDisplayName !== undefined) {
        setDisplayName(updatedDisplayName || '');
      }
      if (updatedProfileImage !== undefined) {
        setProfileImage(convertToImageUrl(updatedProfileImage, ''));
        setHasProfileImageError(false);
      }
    };

    window.addEventListener('profileUpdated', handleProfileUpdated);
    window.addEventListener('alertsUpdated', handleAlertsUpdated);
    window.addEventListener('companyChanged', handleCompanyChanged);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdated);
      window.removeEventListener('alertsUpdated', handleAlertsUpdated);
      window.removeEventListener('companyChanged', handleCompanyChanged);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearRefreshTimer();
    };
  }, [refreshAlertsPermission, refreshUnreadCount, scheduleRefresh, clearRefreshTimer]);

  useEffect(() => {
    if (canReadAlerts) {
      refreshUnreadCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadAlerts]);

  const renderAvatarContent = (avatarClassName) => {
    if (profileImage && !hasProfileImageError) {
      return (
        <img
          src={profileImage}
          alt="Profile"
          className={avatarClassName}
          onError={() => setHasProfileImageError(true)}
        />
      );
    }
    return <i className="fa fa-user"></i>;
  };

  const handleLogout = async () => {
    await LoginService.logout();
  };

  return (
    <>
      <header className="header">
        
        <div className="logo-container">
          <div className="visible-xs toggle-sidebar-left" onClick={toggleSidebar}>
            <i className="fa fa-bars" aria-label="Toggle sidebar"></i>
          </div>
          <div className="logo">
            <div className="font-logo custom-primary-color">
              <DynamicLogo alt="Logo" className="logo-img" />
            </div>
          </div>
        </div>

        <div className="header-right">
          <CompanySelector />
          <LanguageSelector />

          {canReadAlerts && (
            <button
              type="button"
              className="header-alerts-button"
              onClick={() => navigate('/alerts')}
              aria-label={t('alert.open', { defaultValue: 'Abrir alertas' })}
              title={t('alert.open', { defaultValue: 'Abrir alertas' })}
            >
              <span className="header-alerts-icon">
                <FontAwesomeIcon icon={faBell} />
                {unreadAlertsCount > 0 && (
                  <span className="header-alerts-badge" aria-label={String(unreadAlertsCount)}>
                    {unreadAlertsCount > 99 ? '99+' : unreadAlertsCount}
                  </span>
                )}
              </span>
            </button>
          )}
          
          <div className="modern-userbox">
            <button 
              type="button" 
              className="userbox-toggle"
              onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
            >
              <div className="user-avatar">
                {renderAvatarContent('user-avatar-image')}
              </div>
              <div className="user-info">
                <span className="user-name">{username}</span>
                <span className="user-role">{userRole ? t(userRole) : ''}</span>
              </div>
              <i className={`fa fa-chevron-down user-chevron ${isUserDropdownOpen ? 'open' : ''}`}></i>
            </button>

            {isUserDropdownOpen && (
              <>
                <div className="dropdown-overlay" onClick={() => setIsUserDropdownOpen(false)}></div>
                <div className="modern-dropdown-menu">
                  <div className="dropdown-header">
                    <div className="dropdown-user-info">
                      <div className="dropdown-avatar">
                        {renderAvatarContent('dropdown-avatar-image')}
                      </div>
                      <div>
                        <div className="dropdown-username">{username}</div>
                        <div className="dropdown-userrole">{userRole ? t(userRole) : ''}</div>
                      </div>
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-body">
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => {
                        openTab('/profile', 'profile.title', 'fa-user');
                        setIsUserDropdownOpen(false);
                      }}
                    >
                      <i className="fa fa-id-card dropdown-icon"></i>
                      <span>{t('header.profile')}</span>
                    </button>
                    <button
                      type="button"
                      className="dropdown-item logout-item"
                      onClick={(e) => {
                        e.preventDefault();
                        handleLogout();
                      }}
                    >
                      <i className="fa fa-power-off dropdown-icon"></i>
                      <span>{t('header.logout')}</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
